import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppConfig } from "../../../AppConfig";
import { PageRoutes } from "../../../constants/routes";
import { LocalStorageManager } from "../../../core/storage/LocalStorageManager";
import { appActions } from "../../../ducks/app";
import { campaignSelectors } from "../../../ducks/campaign";
import { AppCoupon, couponActions } from "../../../ducks/coupon";
import { MenuInCart } from "../../../ducks/menu";
import { useNavigation } from "../../../hooks/useNavigation";

export const useEarlyBirdCampaign = () => {
  const { navigateToPage } = useNavigation();
  const campaign = useSelector(campaignSelectors.activeCampaign);
  const dispatch = useDispatch();

  const menusInCart = useMemo(() => {
    return LocalStorageManager.Instance.getObject<MenuInCart[]>(
      AppConfig.Instance.LocalStorageKey["menusInCart"],
    );
  }, []);

  const earlyBirdMenus = useMemo(() => {
    return (menusInCart ?? []).filter((menu) => menu.isEarlyBird);
  }, [menusInCart]);

  const regularMenus = useMemo(() => {
    return (menusInCart ?? []).filter((menu) => !menu.isEarlyBird);
  }, [menusInCart]);

  const handleClearLocalMenuData = useCallback(() => {
    const keysToRemove = [
      "menusInCart",
      "appliedCoupons",
      "selectedAppCoupons",
      "unselectedAppCoupons",
      "appToken",
    ];
    keysToRemove.forEach((key) => {
      LocalStorageManager.Instance.removeObject(
        AppConfig.Instance.LocalStorageKey[key],
      );
    });
  }, []);

  const handleSaveCampaignAndNavigate = useCallback(() => {
    LocalStorageManager.Instance.saveObject(
      AppConfig.Instance.LocalStorageKey["campaign"],
      campaign,
    );
    navigateToPage(PageRoutes.HOME, true);
  }, [campaign, navigateToPage]);

  const handleRemoveCampaignAndNavigate = useCallback(() => {
    LocalStorageManager.Instance.removeObject(
      AppConfig.Instance.LocalStorageKey["campaign"],
    );
    navigateToPage(PageRoutes.HOME, true, { isRegular: true });
  }, [navigateToPage]);

  const handleEarlyBirdClick = useCallback(() => {
    const selectedAppCoupons =
      LocalStorageManager.Instance.getObject<AppCoupon[]>(
        AppConfig.Instance.LocalStorageKey["selectedAppCoupons"],
      ) ?? [];
    const shouldShowModal =
      regularMenus?.length > 0 || selectedAppCoupons?.length > 0;

    if (shouldShowModal) {
      dispatch(
        appActions.showModal.started({
          title:
            "現在のカートとクーポンが\nリセットされますが\nよろしいですか？",
          nextButtonLabel: "早割に進む",
          closeButtonLabel: "通常の注文に戻る",
          handleNext: () => {
            dispatch(appActions.closeModal());
            dispatch(couponActions.resetToInitialState());
            handleClearLocalMenuData();
            handleSaveCampaignAndNavigate();
          },
          handleClose: () => {
            dispatch(appActions.closeModal());
          },
        }),
      );
      return;
    }
    handleSaveCampaignAndNavigate();
  }, [
    dispatch,
    handleClearLocalMenuData,
    handleSaveCampaignAndNavigate,
    regularMenus,
  ]);

  const handleRegularOrderClick = useCallback(() => {
    if (earlyBirdMenus?.length) {
      return dispatch(
        appActions.showModal.started({
          title:
            "現在のカートとクーポンが\nリセットされますが\nよろしいですか？",
          nextButtonLabel: "通常の注文に進む",
          closeButtonLabel: "早割に戻る",
          handleNext: () => {
            dispatch(appActions.closeModal());
            handleClearLocalMenuData();
            handleRemoveCampaignAndNavigate();
          },
          handleClose: () => {
            dispatch(appActions.closeModal());
          },
        }),
      );
    }
    handleRemoveCampaignAndNavigate();
  }, [navigateToPage, earlyBirdMenus, handleRemoveCampaignAndNavigate]);

  useEffect(() => {
    if (!campaign) {
      navigateToPage(PageRoutes.HOME, true);
    }
  }, []);

  return {
    campaign,
    handleRegularOrderClick,
    handleEarlyBirdClick,
  };
};
